import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../../utils/apiHelper';
import PaymentForm from './PaymentForm';
import PaymentSummary from './PaymentSummary';
import PaymentActions from './PaymentActions';
import useGiftCard from './useGiftCard';

const PaymentModal = ({ isOpen, onClose, appointment, onPay, loadingState, monto }) => {  // 'monto' es el abono (Sena) del turno actual
  // Estados generales (igual que en el original)
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedServicesByEmployee, setSelectedServicesByEmployee] = useState({});
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sameDayTurnos, setSameDayTurnos] = useState([]);

  // Estado para el código de Gift Card
  const [giftCardCode, setGiftCardCode] = useState('');
  const { giftCard, redeemGiftCard, resetGiftCard } = useGiftCard();

  // Se determina el descuento de la Gift Card usando su saldo actual, si está activa y vigente
  const giftCardAmount =
    giftCard &&
    giftCard.state === 'active' &&
    new Date(giftCard.expires_at) > new Date() &&
    giftCard.current_balance > 0
      ? giftCard.current_balance
      : 0;

  // Al abrir el modal, carga los datos; al cerrarlo, reinicia todos los estados
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        await fetchServices();
        await fetchPaymentMethods();
        await fetchEmpleados();
      };
      fetchData();
    } else {
      // Reinicia todos los estados cuando se cierra el modal
      setPaymentMethod('Efectivo');
      setSelectedEmployee(null);
      setEmployees([]);
      setSelectedServicesByEmployee({});
      setServices([]);
      setSearchTerm('');
      setPaymentMethods([]);
      setSameDayTurnos([]);
      setGiftCardCode('');
      resetGiftCard();
    }
  }, [isOpen, appointment]);

  useEffect(() => {
    if (employees.length > 0) {
      initializeSelectedServices();
    }
  }, [employees, sameDayTurnos]);

  const fetchServices = async () => {
    try {
      const data = await fetchWithAuth('/services/');
      setServices(data);
      fetchSameDayTurnos();
    } catch (error) {
      console.error('Error al obtener los servicios:', error);
      toast.error('Error al obtener los servicios.');
    }
  };

  const fetchPaymentMethods = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth('/administracion/metodos/pago', { method: 'GET' });
      setPaymentMethods(data);
    } catch (error) {
      console.error('Error al obtener los métodos de pago:', error);
      toast.error('Error al obtener los métodos de pago.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSameDayTurnos = async () => {
    try {
      const formattedDate = new Date(appointment.fecha).toISOString().split('T')[0];
      const data = await fetchWithAuth(
        `/turnos/cliente/${appointment.cliente_id}/fecha/${formattedDate}?turno_id=${appointment.turno_id}`,
        { method: 'GET' }
      );
      const sameDayTurnosData = data.filter(turno => turno.turno_id !== appointment.turno_id);
      setSameDayTurnos(sameDayTurnosData);
    } catch (error) {
      console.error('Error al obtener los turnos del mismo día:', error);
      toast.error('Error al obtener los turnos del mismo día.');
    }
  };

  const fetchEmpleados = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth('/empleados/');
      setEmployees(data);
    } catch (error) {
      console.error('Error al obtener los empleados:', error);
      toast.error('Error al obtener los empleados.');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeSelectedServices = () => {
    const initialSelectedServices = {};
    const appointmentEmployee = employees.find(emp => emp.empleado_id === appointment.empleado_id);
    if (appointmentEmployee) {
      initialSelectedServices[appointment.empleado_id] = [
        {
          servicio_id: appointment.servicio_id,
          nombre: services.find(s => s.servicio_id === appointment.servicio_id)?.nombre || '',
          precio: services.find(s => s.servicio_id === appointment.servicio_id)?.precio || 0,
          count: 1,
          empleado_id: appointment.empleado_id
        }
      ];
    }
    sameDayTurnos.forEach(turno => {
      const turnoEmployee = employees.find(emp => emp.empleado_id === turno.empleado_id);
      if (turnoEmployee) {
        if (!initialSelectedServices[turno.empleado_id]) {
          initialSelectedServices[turno.empleado_id] = [];
        }
        initialSelectedServices[turno.empleado_id].push({
          servicio_id: turno.servicio_id,
          nombre: services.find(s => s.servicio_id === turno.servicio_id)?.nombre || '',
          precio: services.find(s => s.servicio_id === turno.servicio_id)?.precio || 0,
          count: 1,
          empleado_id: turno.empleado_id
        });
      }
    });
    setSelectedServicesByEmployee(initialSelectedServices);
    if (appointmentEmployee) {
      setSelectedEmployee(appointmentEmployee);
    }
  };

  // Función para redimir la Gift Card al presionar "Aplicar Código"
  const handleRedeem = async () => {
    if (!giftCardCode) {
      toast.error('Ingresa el código de la Gift Card');
      return;
    }
    try {
      const redeemed = await redeemGiftCard(giftCardCode);
      if (redeemed) {
        toast.success('Gift Card aplicada');
      }
    } catch (err) {
      toast.error('Código de Gift Card no válido o expirado');
    }
  };

  // Función para confirmar el pago. Se envía también el código público de la Gift Card (si existe)
  const handleConfirmPay = async () => {
    const selectedServiceIds = Object.values(selectedServicesByEmployee)
      .flat()
      .flatMap(service =>
        Array(service.count).fill({
          servicio_id: service.servicio_id,
          monto: service.precio,
          empleado_id: service.empleado_id
        })
      );
    // Se envía la gift card si existe (por ejemplo, su código público)
    await onPay(
      appointment.turno_id,
      paymentMethod,
      selectedServiceIds,
      giftCard ? giftCard.public_code : null
    );
    onClose();
  };

  // Cálculo de totales (igual que en el original)
  const totalAmount = Object.values(selectedServicesByEmployee)
    .flat()
    .reduce((total, service) => total + (service.precio || 0) * service.count, 0);
  const selectedPayMethod = paymentMethods.find(method => method.nombre === paymentMethod);
  const surcharge = selectedPayMethod && selectedPayMethod.tiene_recargo
    ? (totalAmount * selectedPayMethod.valor_recargo) / 100
    : 0;
  const totalWithSurcharge = totalAmount + surcharge;
  const totalSameDaySena = sameDayTurnos.reduce((acc, turno) => acc + (turno.sena_monto || 0), 0);
  // Total final: se resta el descuento de la Gift Card y el abono (Sena) de otros turnos
  const rawTotal = totalWithSurcharge - (giftCardAmount || 0) - totalSameDaySena - (monto || 0);
  const totalAmountFinal = Math.max(rawTotal, 0);

  return (
    <Transition show={isOpen} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <Transition.Child
          className="fixed inset-0 transition-opacity"
          aria-hidden="true"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"></div>
        </Transition.Child>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <Transition.Child
          className="inline-block align-bottom bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl w-full max-h-[90vh]"
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="bg-white p-6 md:p-8 overflow-y-auto max-h-[90vh]">
            <h2 className="text-3xl font-semibold text-gray-800 text-center mb-6 tracking-tight">
              Realizar Pago
            </h2>

            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4">
                <PaymentForm
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  employees={employees}
                  selectedEmployee={selectedEmployee}
                  setSelectedEmployee={setSelectedEmployee}
                  services={services}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedServicesByEmployee={selectedServicesByEmployee}
                  setSelectedServicesByEmployee={setSelectedServicesByEmployee}
                  paymentMethods={paymentMethods}
                />
              </div>

              {/* Bloque de redención de Gift Card */}
              <div className="mx-auto bg-white rounded-xl shadow-lg p-6 border border-gray-200 relative">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 text-blue-600 rounded-full p-3 mr-3">
                    <i className="fas fa-gift fa-lg"></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800 tracking-tight">
                    Redime tu Gift Card
                  </h2>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código Gift Card <span className="text-gray-500">(opcional)</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-400">
                    <i className="fas fa-ticket-alt"></i>
                  </span>
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 placeholder-gray-400 transition duration-200 shadow-sm"
                    placeholder="Ingresa el código de la Gift Card"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleRedeem}
                  className="mt-4 w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition duration-200 shadow-md"
                >
                  Aplicar Código
                </button>
                {/* Botón grande y rojo para sacar la Gift Card */}
                {giftCard && (
                  <button
                    onClick={resetGiftCard}
                    className="mt-4 w-full bg-red-600 text-white font-bold py-2 rounded-lg hover:bg-red-700 transition duration-200 shadow-md"
                    title="Sacar Gift Card"
                  >
                    Sacar Gift Card
                  </button>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4">
                <PaymentSummary
                  selectedServicesByEmployee={selectedServicesByEmployee}
                  employees={employees}
                  paymentMethods={paymentMethods}
                  paymentMethod={paymentMethod}
                  sameDayTurnos={sameDayTurnos}
                  giftCard={giftCard}              // Se pasa el objeto giftCard
                  giftCardAmount={giftCardAmount}
                  monto={monto}
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
                <PaymentActions
                  onClose={onClose}
                  onConfirm={handleConfirmPay}
                  loadingState={loadingState}
                  resetGiftCards={resetGiftCard}
                  selectedEmployee={selectedEmployee}
                  selectedServices={selectedEmployee ? selectedServicesByEmployee[selectedEmployee.empleado_id] || [] : []}
                />
              </div>
            </div>
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
};

export default PaymentModal;
